import { type FunctionComponent } from "react";
import { ArrowLeftOutlined, CalendarOutlined, MessageOutlined, TeamOutlined, TrophyOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Badge, Button, Card, Skeleton, Space, Tabs, Tag, Typography } from "antd";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useNavigate, useParams } from "react-router-dom";

import {
  ChallengeCompletionStatus,
  ChallengeGoalType,
  coverPositionStyle,
  type ChallengeEntity,
  type ChallengeStatus,
} from "@fitness-app/data-models/entities/Challenge";
import { asChallengeFeedMode, ChallengeFeedMode } from "@fitness-app/data-models/entities/ChallengeFeed";

import { supabase } from "~/store/initializeStore";
import ChallengeApprovalQueue from "./ChallengeApprovalQueue";
import ChallengeCompletionsTable from "./ChallengeCompletionsTable";
import ChallengeEventsTable from "./ChallengeEventsTable";
import ChallengeLeaderboardTable from "./ChallengeLeaderboardTable";
import ChallengeManagersPanel from "./ChallengeManagersPanel";
import ChallengeParticipantsTable from "./ChallengeParticipantsTable";
import ChallengeRecordingsReview from "./ChallengeRecordingsReview";
import ChallengeSettingsPanel from "./ChallengeSettingsPanel";
import ChallengeTasksTable from "./ChallengeTasksTable";
import {
  challengeFeedModeLabels,
  challengeGoalTypeLabels,
  challengeStatusColors,
  challengeStatusLabels,
} from "./constants";
import { formatChallengeDateRange } from "./helpers";

const challengeTabKeys = [
  "tasks",
  "events",
  "leaderboard",
  "approvals",
  "recordings",
  "participants",
  "completions",
  "managers",
] as const;
type ChallengeTabKey = (typeof challengeTabKeys)[number];

const ChallengeDetailPage: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Otwarta zakładka siedzi w URL-u, żeby przetrwała wejście w profil klientki i powrót wstecz.
  const [activeTab, setActiveTab] = useQueryState(
    "tab",
    parseAsStringEnum<ChallengeTabKey>([...challengeTabKeys]).withDefault("tasks"),
  );
  const challengeQuery = useQuery({
    queryKey: ["challenge-detail", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge")
        .select("*")
        .eq("id", id ?? "")
        .single<ChallengeEntity>();

      if (error) {
        throw error;
      }

      return data;
    },
  });

  const pendingCountQuery = useQuery({
    queryKey: ["challenge-pending-count", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { count, error } = await supabase
        .from("challenge_task_completion")
        .select("id", { count: "exact", head: true })
        .eq("challengeId", id ?? "")
        .eq("status", ChallengeCompletionStatus.Pending);

      if (error) {
        throw error;
      }

      return count ?? 0;
    },
  });

  if (!id) {
    return null;
  }

  const challenge = challengeQuery.data;
  const status = challenge?.status as ChallengeStatus | undefined;
  const feedMode = asChallengeFeedMode(challenge?.feedMode);
  const goalType = (challenge?.goalType as ChallengeGoalType | undefined) ?? ChallengeGoalType.Leaderboard;

  return (
    <Card>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Button type="link" className="px-0" icon={<ArrowLeftOutlined />} onClick={() => navigate("/challenges")}>
          Powrót do listy
        </Button>

        {challengeQuery.isLoading ? (
          <Skeleton active avatar paragraph={{ rows: 2 }} />
        ) : (
          <div className="flex flex-wrap items-start gap-5">
            {challenge?.coverImage ? (
              <img
                src={challenge.coverImage}
                alt=""
                className="h-[99px] w-[176px] shrink-0 rounded-xl object-cover"
                style={{ objectPosition: coverPositionStyle(challenge.coverPositionX, challenge.coverPositionY) }}
              />
            ) : null}

            <div className="min-w-[280px] flex-1">
              <Space size={8} align="center" wrap>
                <Typography.Title level={3} style={{ margin: 0 }}>
                  {challenge?.name}
                </Typography.Title>
                {status ? (
                  <Tag color={challengeStatusColors[status] ?? "default"}>
                    {challengeStatusLabels[status] ?? status}
                  </Tag>
                ) : null}
              </Space>

              {challenge?.description ? (
                <div>
                  <Typography.Text type="secondary">{challenge.description}</Typography.Text>
                </div>
              ) : null}

              {/* Read-only summary of what is set — the controls themselves live in the panel below,
                  so this line stays scannable instead of being a row of dropdowns. */}
              <Space size={4} split={<Typography.Text type="secondary">·</Typography.Text>} wrap className="mt-2">
                <Typography.Text type="secondary">
                  <CalendarOutlined /> {formatChallengeDateRange(challenge?.startDate, challenge?.endDate)}
                </Typography.Text>
                <Typography.Text type="secondary">
                  <TeamOutlined /> {challenge?.audience ? "wybrany segment" : "cała organizacja"}
                </Typography.Text>
                <Typography.Text type="secondary">
                  <TrophyOutlined /> {challengeGoalTypeLabels[goalType]}
                </Typography.Text>
                <Typography.Text type="secondary">
                  <MessageOutlined /> {challengeFeedModeLabels[feedMode]}
                </Typography.Text>
                {challenge?.joinCode ? (
                  <Typography.Text type="secondary">
                    Kod:{" "}
                    <Typography.Text copyable code>
                      {challenge.joinCode}
                    </Typography.Text>
                  </Typography.Text>
                ) : null}
              </Space>
            </div>

            {feedMode !== ChallengeFeedMode.None ? (
              <Button
                type="primary"
                ghost
                icon={<MessageOutlined />}
                onClick={() => navigate(`/challenges/${id}/tablica`)}
              >
                Otwórz tablicę
              </Button>
            ) : null}
          </div>
        )}

        <ChallengeSettingsPanel challengeId={id} challenge={challenge} />

        <Tabs
          destroyInactiveTabPane
          activeKey={activeTab}
          onChange={(key) => void setActiveTab(key as ChallengeTabKey)}
          items={[
            {
              key: "tasks",
              label: "Zadania",
              children: <ChallengeTasksTable challengeId={id} />,
            },
            {
              key: "events",
              label: "Kalendarz",
              children: (
                <ChallengeEventsTable
                  challengeId={id}
                  challengeStartDate={challenge?.startDate}
                  challengeEndDate={challenge?.endDate}
                  challengeFeedMode={challenge?.feedMode}
                />
              ),
            },
            {
              key: "leaderboard",
              label: "Ranking",
              children: <ChallengeLeaderboardTable challengeId={id} />,
            },
            {
              key: "approvals",
              label: (
                <Badge count={pendingCountQuery.data ?? 0} offset={[12, -2]} size="small">
                  Do akceptacji
                </Badge>
              ),
              children: <ChallengeApprovalQueue challengeId={id} />,
            },
            {
              key: "recordings",
              label: "Nagrania",
              children: <ChallengeRecordingsReview challengeId={id} />,
            },
            {
              key: "participants",
              label: "Uczestnicy",
              children: <ChallengeParticipantsTable challengeId={id} />,
            },
            {
              key: "completions",
              label: "Zaliczenia",
              children: <ChallengeCompletionsTable challengeId={id} />,
            },
            {
              key: "managers",
              label: "Zarządzający",
              children: <ChallengeManagersPanel challengeId={id} />,
            },
          ]}
        />
      </Space>
    </Card>
  );
};

export default ChallengeDetailPage;
